import React, { useState, useEffect, useRef } from 'react'
import { useHistory, useParams, useLocation, Link } from 'react-router-dom'
import { Menu, MenuItem, Paper, Skeleton } from '@mui/material'
import Api from '../../../_helpers/Api'
import { trans } from '../../../_providers/Translation'
import { useAuthDataContext } from '../../../_providers/Auth'
import * as Constants from '../../../_config/app'
import { TYPE } from '../../../_constants/Reservation'
import AddOrEditSmsMessage from '../sms/text-messages/AddOrEdit'

// partials
import FilterTabs from './partials/FilterTabs'
import ViewInfo from './partials/ViewInfo'
import Parts from './partials/parts/Index'
import Images from './partials/Images'
import Comments from './partials/Comments'
import Todo from './partials/Todo'
import Invoices from './partials/Invoices'
import History from './partials/History'

// modals
import Success from '../../modals/messages/Success'
import Error from '../../modals/messages/Error'
import Question from '../../modals/messages/Question'
import AddComment from './partials/modals/AddComment'

import loader from '../../../assets/img/loader.svg'

// images
import dropdownArrowImage from '@/assets/img/icons/select-arrow.svg'
import complaintImage from '@/assets/img/reservations/icons/complaint.svg'
import continueImage from '@/assets/img/reservations/icons/continue.svg'
import createNewImage from '@/assets/img/reservations/icons/create-new.svg'
import duplicationImage from '@/assets/img/reservations/icons/duplication.svg'
import removeImage from '@/assets/img/reservations/icons/remove.svg'
import sendNotificationImage from '@/assets/img/reservations/icons/send-notification.svg'
import sendSmsImage from '@/assets/img/reservations/icons/send-sms.svg'
import sendOtherImage from '@/assets/img/reservations/icons/send-other.svg'
import editImage from '@/assets/img/reservations/icons/edit.svg'
import toggleReservationCloseIcon from '@/assets/img/reservations/icons/toggle-reservation-close.svg'
import toggleReservationOpenIcon from '@/assets/img/reservations/icons/toggle-reservation-open.svg'

// styles
import '@/assets/scss/pages/reservations.scss'

// hooks
import useReservationOpenToggle from '@/_helpers/useReservationOpenToggle'

function View() {

    const { loadingReservationOpenToggle, toggleReservationOpen } = useReservationOpenToggle()

    const params = useParams()
    const history = useHistory()
    const location = useLocation()
    const auth = useAuthDataContext()

    const id = params.id
    const searchParams = new URLSearchParams(location.search)

    const tabs = ['info', 'parts', 'images', 'todo', 'comments', 'invoices', 'history']
    const tab = searchParams.get('tab')
    const urlIndicator = searchParams.get('indicator')

    const [reservation, setReservation] = useState({
        tab: tabs.indexOf(tab) > -1 ? tab : tabs[0],
        data: {
            contractor: {},
            customer: {},
            appliancecategory: {},
            appliancebrand: {},
            appliancemodel: {},
        },
        tabsCount: {
            commentsCount: 0,
            imagesCount: 0,
            partsCount: 0,
            todosCount: 0,
            manualsCount: 0,
            invoicesCount: 0,
        },
        update: false,
        loading: true,
    })

    const [openOptions, setOpenOptions] = useState(null)
    const [openApplianceDropdown, setOpenApplianceDropdown] = useState(false)
    const [messageTemplates, setMessageTemplates] = useState([])
    const [loading, setLoading] = useState(false)

    const optionsButtonRef = useRef(null)
    const messageTemplatesButtonRef = useRef(null)
    const smsModalRef = useRef(null)
    const successModalRef = useRef(null)
    const errorModalRef = useRef(null)
    const questionRemoveModalRef = useRef(null)
    const addCommentRef = useRef(null)

    useEffect(() => {
        setReservation(prev => ({
            ...prev,
            tab: tabs.indexOf(tab) > -1 ? tab : tabs[0],
        }))
    }, [location.search])

    useEffect(() => {
        setReservation(prev => ({ ...prev, loading: true }))

        Api.get(`reservations/find?id=${id}`)
            .then(res => {
                setReservation(prev => ({
                    ...prev,
                    data: res.data,
                    tabsCount: {
                        commentsCount: res.data.comments_count,
                        imagesCount: res.data.images_count,
                        partsCount: res.data.parts_count,
                        todosCount: res.data.todos_count,
                        manualsCount: res.data.manuals_count,
                        invoicesCount: res.data.invoices_count,
                    },
                }))
            })
            .catch(err => {
                if (err.response?.status === 404) {
                    history.push('/schedule/reservations')
                }
            })
            .finally(() => {
                setReservation(prev => ({ ...prev, loading: false }))
            })
    }, [id, urlIndicator, reservation.update])

    const handleTab = newValue => {
        const currentParams = new URLSearchParams(location.search)
        currentParams.set('tab', newValue)
        history.push(`?${currentParams.toString()}`)
        setReservation(prev => ({ ...prev, tab: newValue }))
    }

    const refreshData = () => {
        setReservation(prev => ({ ...prev, update: new Date().getTime() }))
    }

    const data = reservation.data

    const handleEdit = () => {
        setOpenOptions(null)
        history.push(`/schedule/reservations/edit/${data.id}`)
    }

    const handleCreate = () => {
        setOpenOptions(null)
        const type = Number(data.res_type_id) === TYPE.ADDRESS ? 'address' : 'service'
        history.push(`/schedule/reservations/${type}/add`)
    }

    const handleContinue = () => {
        setOpenOptions(null)
        Api.post('reservations/continue', { id: data.id })
            .then(res => res.data.id && history.push(`/schedule/reservations/edit/${res.data.id}`))
            .catch(() => errorModalRef.current.open())
    }

    const handleReplicate = () => {
        setOpenOptions(null)
        Api.post('reservations/replicate', { id: data.id })
            .then(res => res.data.id && history.push(`/schedule/reservations/edit/${res.data.id}`))
            .catch(() => errorModalRef.current.open())
    }

    const handleReclamation = () => {
        setOpenOptions(null)
        Api.post('reservations/reclamation', { id: data.id })
            .then(res => res.data.id && history.push(`/schedule/reservations/edit/${res.data.id}`))
            .catch(() => errorModalRef.current.open())
    }

    const handleSendSms = () => {
        setOpenOptions(null)
        Api.post('reservations/send-sms-details', { id: data.id })
            .catch(() => errorModalRef.current.open())
    }

    const handleSendConfirmation = () => {
        setOpenOptions(null)
        Api.post('reservations/send-confirmation', { id: data.id })
            .catch(() => errorModalRef.current.open())
    }

    const handleSendReport = () => {
        setOpenOptions(null)
        Api.post('reservations/send-report', { id: data.id })
            .then(res => res.data.success ? successModalRef.current.open() : errorModalRef.current.open())
            .catch(() => errorModalRef.current.open())
    }

    const handleShowPdfOrder = () => {
        setOpenOptions(null)
        window.open(Constants.App.api + 'reservations/pdf-order?id=' + data.id + '&token=' + auth.token(), '_blank')
    }

    const handleSendCompletion = () => {
        setOpenOptions(null)
        Api.post('reservations/send-completion-details', { id: data.id })
            .then(res => res.data.success ? successModalRef.current.open() : errorModalRef.current.open())
            .catch(() => errorModalRef.current.open())
    }

    const fetchMessageTemplates = async () => {
        if (messageTemplates.length > 0) return
        setLoading(true)
        try {
            const response = await Api.get('text-messages/all')
            setMessageTemplates(response.data.data)
        } catch (error) {
            console.error('Error fetching messageTemplates:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleOptionClick = async (option) => {
        try {
            smsModalRef.current.edit(option.id, { customHeading: 'customHeading' })
        } catch (error) {
            console.error('Error processing option:', error)
        }
    }

    function handleToggleReservationOpen() {
        setOpenOptions(null)

        // If the reservation is open, force the user to add a comment first.
        // Only after the comment is successfully saved do we toggle it.
        if (data.is_open) {
            addCommentRef.current.open(data.id)
            return
        }

        toggleReservationOpen(!data.is_open, data.id, refreshData)
    }

    const handleCommentAddedBeforeToggle = () => {
        toggleReservationOpen(!data.is_open, data.id, refreshData)
    }

    const handleDelete = () => {
        setOpenOptions(null)
        const modal = questionRemoveModalRef.current
        modal.open()
        modal.onSuccess(() => {
            Api.post('reservations/delete', { id: data.id })
                .then(() => history.push('/schedule/reservations?page=1'))
        })
    }

    const ReservationStatus = () => {
        const status = (!data.initial_status_id || data.initial_status_id === '')
            ? 'no'
            : (data.final_status_id !== null && data.final_status_id !== '' && data.final_status_id)
                ? 'completed'
                : 'active'

        const statusConfig = {
            completed: { label: trans('labels.reservation-status.completed'), color: '#3A61FF' },
            active: { label: trans('labels.reservation-status.active'), color: '#89B9B9' },
            no: { label: '', color: '#D4D9EE' },
        }

        return (
            <div className="status-in-heading">
                <span style={{ backgroundColor: statusConfig[status].color }} />
                {statusConfig[status].label}
            </div>
        )
    }

    const [sharedActionLoading, setSharedActionLoading] = useState(false)

    const handleAcceptShared = async () => {
        const sharedId = reservation.data.shared_reservation_main_db_id
            || reservation.data.shared_reserv_id
            || reservation.data.sharedReservationId

        if (!sharedId) return
        setSharedActionLoading(true)
        try {
            await Api.post(`shared-reservations/${sharedId}/accept`)
            refreshData()
        } catch (e) {
            console.error('[SharedReserv] accept error:', e)
        } finally {
            setSharedActionLoading(false)
        }
    }

    const handleDeclineShared = async () => {
        const sharedId = reservation.data.shared_reservation_main_db_id
            || reservation.data.shared_reserv_id
            || reservation.data.sharedReservationId

        if (!sharedId) return
        setSharedActionLoading(true)
        try {
            await Api.post(`shared-reservations/${sharedId}/decline`)
            history.push('/reservations')
        } catch (e) {
            console.error('[SharedReserv] decline error:', e)
        } finally {
            setSharedActionLoading(false)
        }
    }


    return (
        <div className="wrapper">
            <Success
                mainMessage={trans('headings.success14')}
                ref={successModalRef}
            />
            <Error
                mainMessage={trans('headings.error3')}
                ref={errorModalRef}
            />
            <Question
                ref={questionRemoveModalRef}
                mainMessage={trans('headings.confirmDelete')}
                secondaryMessage={trans('subheadings.confirmDelete')}
            />

            <AddOrEditSmsMessage ref={smsModalRef} />

            <AddComment
                ref={addCommentRef}
                callback={handleCommentAddedBeforeToggle}
            />

            <div className="top-part">
                <div className="left">
                    <h1
                        className="page-heading"
                        style={{ display: 'flex', alignItems: 'center', gap: '20px' }}
                    >
                        {`${trans('headings.viewReservation')} #${data.reference_code || ''}`}
                        {!reservation.loading && <ReservationStatus />}
                    </h1>
                    <p className="page-subheading">
                        {trans('subheadings.createEditReservations')}
                    </p>
                </div>
                {!reservation.loading &&
                    <div className="right">
                        <Menu
                            anchorEl={openOptions}
                            keepMounted
                            open={Boolean(openOptions)}
                            onClose={() => setOpenOptions(null)}
                            slotProps={{
                                paper: {
                                    style: { overflow: 'visible' }
                                }
                            }}
                            className="reservation-options-menu"
                        >
                            <MenuItem onClick={handleEdit}>
                                <img src={editImage} alt="edit" />
                                {trans('buttons.edit')}
                            </MenuItem>
                            <MenuItem onClick={handleCreate}>
                                <img src={createNewImage} alt="create new" />
                                {trans('buttons.new')}
                            </MenuItem>
                            {data.can_make_reclamation &&
                                <MenuItem onClick={handleReclamation}>
                                    <img src={complaintImage} alt="reclamation" />
                                    {trans('buttons.reclamation')}
                                </MenuItem>
                            }
                            {data.can_make_continuation &&
                                <MenuItem onClick={handleContinue}>
                                    <img src={continueImage} alt="continue" />
                                    {trans('buttons.continuation')}
                                </MenuItem>
                            }
                            <MenuItem onClick={handleReplicate}>
                                <img src={duplicationImage} alt="duplicate" />
                                {trans('buttons.duplicate')}
                            </MenuItem>
                            {data.final_status_id && data.final_status_id !== 13 ?
                                <>
                                </>
                                :
                                <MenuItem onClick={handleToggleReservationOpen}>
                                    <img src={data.is_open ? toggleReservationCloseIcon : toggleReservationOpenIcon} alt="toggle reservation" />
                                    {data.is_open ? trans('buttons.close') : trans('buttons.open')}
                                </MenuItem>
                            }
                            <hr />
                            <MenuItem onClick={handleSendSms}>
                                <img src={sendSmsImage} alt="send message" />
                                {trans('buttons.sendMessage')}
                            </MenuItem>
                            {data.can_send_confirmation &&
                                <MenuItem onClick={handleSendConfirmation}>
                                    <img src={sendNotificationImage} alt="send notification" />
                                    {trans('buttons.sendNotification')}
                                </MenuItem>
                            }
                            <hr />
                            <MenuItem
                                onMouseEnter={() => {
                                    setOpenApplianceDropdown(true)
                                    fetchMessageTemplates()
                                }}
                                onMouseLeave={() => setOpenApplianceDropdown(false)}
                                ref={messageTemplatesButtonRef}
                                className='with-menu'
                            >
                                <img src={sendOtherImage} alt="send other" />
                                {trans('buttons.sendOtherNotification')}
                                {Boolean(openApplianceDropdown) ?
                                    <Paper
                                        sx={{
                                            position: "absolute",
                                            right: 'calc(100% - 10px)',
                                            top: 0,
                                            width: "245px"
                                        }}
                                        className='reservation-options-paper'
                                    >
                                        <b
                                            style={{
                                                padding: '15px',
                                                paddingBottom: 0,
                                                display: 'block',
                                            }}
                                        >
                                            {trans('buttons.chooseFromTemplates')}
                                        </b>
                                        {loading ? (
                                            <div style={{ padding: '15px', paddingTop: '5px' }}>
                                                <Skeleton height={30} />
                                                <Skeleton height={30} />
                                                <Skeleton height={30} />
                                            </div>
                                        ) : messageTemplates.length > 0 ? (
                                            <>
                                                {messageTemplates.map(option => (
                                                    <MenuItem key={option.id}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            handleOptionClick(option)
                                                            setOpenOptions(null)
                                                        }}>
                                                        {option.subject}
                                                    </MenuItem>
                                                ))}
                                            </>
                                        ) : (
                                            <div style={{ padding: '15px', paddingTop: '5px' }}>
                                                {trans('buttons.noTemplatesOptions')}
                                            </div>
                                        )}
                                    </Paper>
                                    :
                                    <></>
                                }
                            </MenuItem>
                            {data.can_send_completion_message || data.can_send_report || data.can_make_pdf_order ?
                                <hr />
                                : ''}
                            {data.can_send_completion_message &&
                                <MenuItem onClick={handleSendCompletion}>
                                    {trans('buttons.sendNotification2')}
                                </MenuItem>
                            }
                            {data.can_send_report &&
                                <MenuItem onClick={handleSendReport}>
                                    {trans('buttons.sentReport')}
                                </MenuItem>
                            }
                            {data.can_make_pdf_order &&
                                <MenuItem onClick={handleShowPdfOrder}>
                                    {trans('buttons.downloadOffer')}
                                </MenuItem>
                            }
                            {!data.is_copy &&
                                <>
                                    <hr />
                                    <MenuItem onClick={handleDelete} sx={{ color: '#EC5252' }}>
                                        <img src={removeImage} alt="delete" />
                                        {trans('buttons.delete')}
                                    </MenuItem>
                                </>
                            }
                        </Menu>
                        <button
                            className="button-default"
                            ref={optionsButtonRef}
                            onClick={() => setOpenOptions(optionsButtonRef.current)}
                        >
                            {trans('buttons.reservationOptions')}
                            <img src={dropdownArrowImage} alt="dropdown arrow" />
                        </button>
                    </div>
                }
            </div>

            {!reservation.loading && (data.is_child || (data.children && data.children.length > 0)) &&
                <div className="reservation-relations">
                    {data.is_child &&
                        <Link to={`/schedule/reservations/view/${data.parent_id}`} className="relation-pill">
                            {String(data.reclamation) === '1' ? trans('buttons.reclamation') : trans('buttons.continuation')}
                            {' '}№{data.parent ? data.parent.reference_code : ''}
                        </Link>
                    }
                    {data.children && data.children.map(c =>
                        <Link key={c.id} to={`/schedule/reservations/view/${c.id}`} className="relation-pill">
                            {c.initialstatus && c.initialstatus.translation ? c.initialstatus.translation.name : ''} №{c.reference_code}
                        </Link>
                    )}
                </div>
            }

            {reservation.data.is_shared_reserv_awaiting_response_by_me && (
                <div className="alert warning" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ flex: 1 }}>{trans('messages.sharedReservationNotRespondedYet')}</span>
                    <button
                        className="button button-success button-small"
                        onClick={handleAcceptShared}
                        disabled={sharedActionLoading}
                    >
                        {sharedActionLoading ? '...' : 'Приеми'}
                    </button>
                    <button
                        className="button button-danger button-small"
                        onClick={handleDeclineShared}
                        disabled={sharedActionLoading}
                    >
                        {sharedActionLoading ? '...' : 'Отхвърли'}
                    </button>
                </div>
            )}

            {reservation.data.is_copy && reservation.data.share_status === "declined" ? (
                <div className="alert warning" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ flex: 1 }}>{trans('messages.declinedReservation')}</span>
                </div>
            ) : <></>}

            <div style={data.is_shared_reserv_awaiting_response_by_me ? { pointerEvents: 'none', opacity: 0.5 } : {}}>
                <FilterTabs
                    editMode={true}
                    tab={reservation.tab}
                    loading={reservation.loading}
                    reservationId={id}
                    referenceCode={data.reference_code}
                    categoryId={data.appliance_category_id}
                    brandId={data.appliance_brand_id}
                    modelId={data.appliance_model_id}
                    model={data.appliancemodel ? data.appliancemodel.name : ''}
                    tabsCount={reservation.tabsCount}
                    handleTab={handleTab}
                />

                {reservation.loading
                    ? <img src={loader} alt="loading" />
                    : reservation.tab === 'info'
                        ? <ViewInfo data={data} />
                        : (() => {
                            switch (reservation.tab) {
                                case 'parts':
                                    return <Parts data={data} refreshData={refreshData} />
                                case 'images':
                                    return <Images data={data} refreshData={refreshData} />
                                case 'todo':
                                    return <Todo data={data} refreshData={refreshData} />
                                case 'comments':
                                    return <Comments data={data} refreshData={refreshData} />
                                case 'invoices':
                                    return <Invoices data={data} refreshData={refreshData} />
                                case 'history':
                                    return <History data={data} refreshData={refreshData} />
                                default:
                                    return ''
                            }
                        })()
                }
            </div>
        </div>
    )
}

export default View
